/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useErrorHandler } from 'react-error-boundary';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';

import CustomReserveConfirmation from '../../api/reserve/customReserveConfirmation';
import configMastaApi from '../../api/masta/configMasta';
import { useDidMount, useNavigateWithId } from '../../customHooks';
import { clear } from '../../redux_toolkit/reserveSlice';
import TopicPath from '../../components/TopicPath';
import BackButton from '../../components/BackButton';
import operationalLogs from '../../api/log/operationalLogs';
import {
  INPUT_TYPE_BIRTHDAY,
  INPUT_DATE_FLAG,
  INPUT_EXP_FLAG,
  RSV_FLAG_PHONE,
  CANCEL_ANNOUNCE,
  DEFAULT_HOME_USE_LABEL,
} from '../../constants';
import { dispTellStartPeople } from '../../utils';
import MoveTop from '../../CommonParts';

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.locale('ja');

function CustomFormConfirmation() {
  const navigate = useNavigateWithId();
  const dispatch = useDispatch();
  const handleError = useErrorHandler();

  const reserve = useSelector((state) => state.reserve);
  const { rsvSetId, useHome, place = '', lgCd = '' } = reserve;
  const {
    name: placeName = '',
    address: placeAddress = '',
    subAddress: placeSubAddress = '',
    tel: placeTel = '',
  } = place;
  const rsvConfig = useSelector((state) => state.rsvConfig);
  const rsvFlag = useSelector((state) => state.reserve.rsvFlag);
  const isLoggedIn = useSelector((state) => state.login.accessToken);

  // 入力情報の取得
  const answerList = useSelector((state) => state.reserve.answerList);

  const { topicPath = [], locationSetting = {} } = rsvConfig;
  const homeUseLabel = locationSetting?.item_label?.home_use || DEFAULT_HOME_USE_LABEL;

  const [over, setOver] = useState(false);
  const [fromMailAddress, setFromMailAddress] = useState('xxx@xxxxxx.ne.jp');
  const [pageName, setPageName] = useState('');
  const [backUrl, setBackUrl] = useState('');

  const [tellStartPeople, setTellStartPeople] = useState(1);

  // 予約設定マスタの検索
  const getReserveSetting = async () => {
    const result = await CustomReserveConfirmation.searchReserveSetting(rsvSetId).catch((err) => {
      window.alert('予約受付設定の取得に失敗しました。');
      handleError(err);
    });
    const { rsvLimit = '', tellStart = 0 } = result;
    setTellStartPeople(tellStart);
    return rsvLimit;
  };

  // 自分の予約数と１人ができる予約の最大数の比較
  const getReserveCount = async () => {
    const { mailAddress, sendType, telNum } = reserve;
    const { count } = await CustomReserveConfirmation.searchReserveCount(
      rsvSetId,
      sendType,
      mailAddress,
      telNum
    ).catch((err) => {
      window.alert('予約受付情報の取得に失敗しました。');
      handleError(err);
    });
    return count;
  };

  // パンくずリスト設定から、ページ名・前の画面のURLをstateに設定
  const getBackUrlAndPageName = (topicPathConf) => {
    const locationPath = window.location.pathname;
    const tmpPath1 = locationPath.replace('/reserve/', '');
    const tmpPath2 = tmpPath1.replace('/test', '');
    const currentPath = tmpPath2.replace('/demo', '');

    let backUrl = '';
    let scnName = '';
    for (let i = 0; i < topicPathConf.length; i += 1) {
      const { url = '', scnName: tmpScnName = '' } = topicPathConf[i];
      if (currentPath === url) {
        // 「前に戻る」先のURL取得
        const { url: tmpBackUrl = '' } = topicPathConf[i - 1];
        if (tmpBackUrl === '/:lgCd') {
          backUrl = lgCd;
        } else {
          backUrl = tmpBackUrl;
        }

        // pageNameを取得
        scnName = tmpScnName;

        break;
      }
    }

    setPageName(scnName);
    setBackUrl(backUrl);
  };

  //  予約日時等を構成する。
  const displayRecItems = () => {
    const result = [];
    const recItems = reserve.recItem;
    const { business } = reserve;

    const businessKeys = Object.keys(business);
    for (let i = 0; i < businessKeys.length; i += 1) {
      const nowBusinessKey = businessKeys[i];
      const { businessName = '' } = business[nowBusinessKey];
      const targetRecItems = recItems.filter(
        (item) => parseInt(item.businessId, 10) === parseInt(nowBusinessKey, 10)
      );

      const recItemKeys = Object.keys(targetRecItems);
      if (recItemKeys.length > 0) {
        result.push(<h3 key={businessName}>{businessName}</h3>);
      }

      const tmpItems = {};
      for (let j = 0; j < recItemKeys.length; j += 1) {
        const nowRecItem = recItemKeys[j];
        const { groupId, groupName, recItemId, recItemName } = targetRecItems[nowRecItem];
        if (!tmpItems[groupId]) {
          tmpItems[groupId] = { groupName, items: [] };
        }

        tmpItems[groupId].items[recItemId] = (
          <li style={{ marginLeft: '24px' }} key={`item_${j}`}>
            {recItemName}
          </li>
        );
      }

      const items = [];
      Object.keys(tmpItems).map((e) => {
        items.push(<strong key={`item_groupName_${i}`}>■{tmpItems[e].groupName}</strong>);
        items.push(<ul key={`item_tmp_${i}`}>{tmpItems[e].items}</ul>);
      });

      result.push(items);
    }

    return result;
  };

  //   各種設定等の取得
  const init = async () => {
    // ページ名・戻り先URLの取得
    getBackUrlAndPageName(topicPath);

    const max = await getReserveSetting();
    const count = await getReserveCount();
    displayRecItems();

    const fromMailConf = await configMastaApi.findByConfCd('rsv_from_mailaddress').catch((err) => {
      window.alert('予約受付設定の取得に失敗しました。');
      handleError(err);
    });
    const { value: fromMail = '' } = fromMailConf;

    // 職員（ログイン済み）による予約は予約制限の対象外とする
    if (!isLoggedIn && count >= max) {
      setOver(true);
    }

    setFromMailAddress(fromMail);
  };

  useDidMount(() => {
    init();
  });

  //   確定ボタン押下後
  const registerReception = async () => {
    if (over) {
      alert(
        '予約可能数の上限に達しました。\n他の予約をキャンセルするか、予約した窓口が完了後に、予約をしてください。'
      );
      return;
    }

    const {
      mailAddress = '',
      rsvDateWithTime = '',
      timearea = '',
      business = {},
      recItem = [],
      numOfAttendees = 0,
      note = '',
      sendType = '',
      rsvFlag = 0,
      telNum = '',
      baseName = '',
      formSetId = '',
    } = reserve;

    try {
      const { receptionId, msg = '' } = await CustomReserveConfirmation.customRegisterReception(
        reserve.lgCd,
        { answerList, telNum, mailAddress, numOfAttendees, note, sendType },
        business,
        recItem,
        { rsvDate: rsvDateWithTime, timearea },
        '', // userCd 予約の場合は空でよい
        rsvSetId,
        place,
        useHome,
        rsvFlag,
        baseName,
        formSetId
      );
      if (msg) {
        window.alert(msg);
        return;
      }
      dispatch(clear());
      navigate('/cmplt', { state: { receptionId, rsvSetId, sendType } });

      // 処理記録
      operationalLogs.createOprLogs('', '', '040000001', 'RsvOnlyWithoutUser', receptionId);
    } catch (err) {
      window.alert('予約登録に失敗しました。');
    }
  };

  //   入力した内容を表示する。
  const makeResidentsDetail = () => {
    const details = [];
    for (let i = 0; i < answerList.length; i += 1) {
      const { question = '', answer = [], inputType = 1 } = answerList[i];
      if (inputType !== INPUT_EXP_FLAG && answer[0] !== '' && answer.length !== 0) {
        details.push(
          <dl key={`makeDetail_${i}`}>
            <dt className="textCnfCmp characterDown">{question}</dt>
            <dd className="textCnfCmp characterDown">
              {inputType === INPUT_DATE_FLAG || inputType === INPUT_TYPE_BIRTHDAY
                ? dayjs(answer[0]).format('YYYY[/]MM[/]DD')
                : answer.join(',')}
            </dd>
          </dl>
        );
      }
    }
    return details;
  };

  return (
    <>
      <TopicPath />
      <main>
        <article>
          <div id="contents">
            <MoveTop lgCd={lgCd} />
            <div className="inner">
              <h2>{pageName}</h2>
              <p className="txtCenter mb40">予約内容を確認してください。</p>
              <table className="tbl_confirmation noBorder mb40">
                <tbody>
                  <tr>
                    <th>利用場所</th>
                    {useHome ? (
                      <td>{homeUseLabel}</td>
                    ) : (
                      <td>
                        {placeName}
                        {placeName && placeAddress ? <br /> : null}
                        {`${placeAddress} ${placeSubAddress}`}
                        {placeAddress && placeTel ? <br /> : null}
                        {placeTel ? `TEL: ${placeTel}` : ''}
                        <br />
                        {rsvFlag === RSV_FLAG_PHONE ? dispTellStartPeople(tellStartPeople) : null}
                      </td>
                    )}
                  </tr>
                  <tr>
                    <th>利用日時</th>
                    <td>{`${dayjs(reserve.rsvDateWithTime).format('YYYY[年]M[月]D[日]')}(${reserve.rsvDayOfWeek
                      })${dayjs(reserve.rsvDateWithTime).format('H[時]mm[分]')}`}</td>
                  </tr>
                  {reserve.recItem.length > 0 ? (
                    <tr>
                      <th>用件</th>
                      <td>{displayRecItems()}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
              <p className="txtCenter">
                <strong>入力情報</strong>
              </p>
              <div className="customInputData textDown">
                {makeResidentsDetail()}
                {reserve.numOfAttendees > 0 ? (
                  <dl>
                    <dt>同席者数</dt>
                    <dd>{reserve.numOfAttendees}人</dd>
                  </dl>
                ) : null}
                <dl>
                  <dt>備考</dt>
                  <dd className="sendConfirm characterDown">{reserve.note}</dd>
                </dl>
                {reserve.sendType === 1 ? (
                  <>
                    <dl>
                      <dt>メール</dt>
                      <dd className="sendConfirm">{reserve.mailAddress}</dd>
                    </dl>
                    <div className="sendConfirm ft_12px">
                      予約が完了しますと、メールが送信されます。
                      <div className="characterDown">
                        {fromMailAddress}からのメール受信が可能か、再度確認してください。
                      </div>
                    </div>
                  </>
                ) : null}
                {reserve.sendType === 2 ? (
                  <>
                    <dl>
                      <dt>SMS</dt>
                      <dd className="sendConfirm">送信する</dd>
                    </dl>
                    <dl>
                      <dt>電話番号（SMS送信用）</dt>
                      <dd>{reserve.telNum}</dd>
                    </dl>
                    <div className="sendConfirm ft_12px">
                      予約が完了しますと、SMSが送信されます。
                      <div className="characterDown">
                        携帯電話でSMSの受信許可の設定を、再度確認してください。
                      </div>
                    </div>
                  </>
                ) : null}
                {reserve.sendType === 3 ? (
                  <>
                    <dl>
                      <dt>SMS</dt>
                      <dd className="sendConfirm">送信する</dd>
                    </dl>
                    <dl>
                      <dt>電話番号（SMS送信用）</dt>
                      <dd>{reserve.telNum}</dd>
                    </dl>
                    <dl>
                      <dt>メール</dt>
                      <dd className="sendConfirm">{reserve.mailAddress}</dd>
                    </dl>
                    <div className="sendConfirm ft_12px">
                      予約が完了しますと、メールとSMSが送信されます。
                      <div className="characterDown">
                        {fromMailAddress}からのメール受信が可能か、再度確認してください。
                        <br />
                        携帯電話でSMSの受信許可の設定を、再度確認してください。
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
              <ul id="navi" className="row jcSB">
                <li className="col2">
                  <BackButton url={`/${backUrl}`} />
                </li>
                <li className="col2 mr0 row jcFE">
                  <a
                    role="button"
                    // href="javascript:void(0);"
                    className="btn btNext"
                    onClick={registerReception}
                    tabIndex={0}
                  >
                    予約を確定する
                  </a>
                </li>
              </ul>
            </div>
            <div className='cancelAnnounce'>{CANCEL_ANNOUNCE}</div>
          </div>
        </article>
      </main>
    </>
  );
}

export default CustomFormConfirmation;
